import React, { useMemo } from 'react';
import { BannerTypes } from '@gin-betting/banners';
import {
    BannerZoneLocation,
    SportEventBannerTheme,
} from '@gin-betting/constants';
import { GQL } from '@gin-betting/core';
import cn from 'classnames';

import { PromoItem } from 'components';
import { AsyncOddButton } from 'modules/betslip/async';
import favorite from 'modules/favorite';
import {
    Action,
    AnalyticAttrs,
    AnalyticAttrsCtx,
    Category,
} from 'services/analytics';
import CMSBanner from '../CMSBanner';

import './banners.scss';

const { ToggleFavoriteButton } = favorite.components;

interface Props {
    banner: BannerTypes.BannerItem;
    zoneLocation: BannerZoneLocation;
}

const Banner: React.FC<Props> = ({ banner, zoneLocation }) => {
    const {
        analyticAttrsForPromo,
        analyticAttrsForSportEvent,
        analyticAttrsForOddBtn,
    } = useMemo(
        () => ({
            analyticAttrsForPromo: getAnalyticAttrsForPromo(zoneLocation),
            analyticAttrsForSportEvent: getAnalyticAttrsForSportEvent(
                zoneLocation
            ),
            analyticAttrsForOddBtn: getAnalyticAttrsForOddBtn(zoneLocation),
        }),
        [zoneLocation]
    );

    switch (banner.type) {
        case GQL.BannerType.betting_live_of_the_day:
        case GQL.BannerType.betting_match_of_the_day:
        case GQL.BannerType.betting_sport_event: {
            const { id: matchId, type } = banner;

            if (!matchId) return null;

            const sharedProps: React.ComponentProps<typeof PromoItem> = {
                matchId,
                AsyncOddButton,
                type,
                favoriteButton: <ToggleFavoriteButton matchId={matchId} />,
            };

            if (zoneLocation === BannerZoneLocation.TOP_SLIDER) {
                if (
                    banner.type === GQL.BannerType.betting_sport_event &&
                    banner.theme === SportEventBannerTheme.Colorful
                ) {
                    return (
                        <div
                            styleName={cn('top-zone-banners', 'is-colorful')}
                            {...analyticAttrsForSportEvent}
                        >
                            <AnalyticAttrsCtx.Provider
                                value={analyticAttrsForOddBtn}
                            >
                                <PromoItem
                                    footer={null}
                                    {...sharedProps}
                                    isColorful={true}
                                />
                            </AnalyticAttrsCtx.Provider>
                        </div>
                    );
                }

                return (
                    <div
                        styleName="top-zone-banners"
                        {...analyticAttrsForSportEvent}
                    >
                        <AnalyticAttrsCtx.Provider
                            value={analyticAttrsForOddBtn}
                        >
                            <PromoItem footer={null} {...sharedProps} />
                        </AnalyticAttrsCtx.Provider>
                    </div>
                );
            }

            return <PromoItem {...sharedProps} />;
        }

        // TODO Ask for design
        // case GQL.BannerType.betting_tournament: {
        //     const { id: tournamentId } = banner;
        //
        //     return (
        //         null
        //     );
        // }

        case GQL.BannerType.cms_banner: {
            return (
                <AnalyticAttrsCtx.Provider value={analyticAttrsForPromo}>
                    <CMSBanner bannerId={banner.id} />
                </AnalyticAttrsCtx.Provider>
            );
        }

        default: {
            return null;
        }
    }
};

const getAnalyticAttrsForSportEvent = (
    zoneLocation: BannerZoneLocation
): Partial<AnalyticAttrs> => {
    switch (zoneLocation) {
        case BannerZoneLocation.TOP_SLIDER:
            return {
                'data-category': Category.BannerTopSlider,
                'data-action': Action.RedirectToSportEvent,
            };
        default:
            return {};
    }
};

function getAnalyticAttrsForPromo(
    zoneLocation: BannerZoneLocation
): Partial<AnalyticAttrs> {
    switch (zoneLocation) {
        case BannerZoneLocation.TOP_SLIDER:
            return {
                'data-category': Category.BannerTopSlider,
            };
        case BannerZoneLocation.RIGHT:
            return {
                'data-category': Category.BannerRight,
            };
        case BannerZoneLocation.RIGHT_SLIDER:
            return {
                'data-category': Category.BannerRightSlider,
            };
        default:
            return {};
    }
}

function getAnalyticAttrsForOddBtn(
    zoneLocation: BannerZoneLocation
): AnalyticAttrs {
    switch (zoneLocation) {
        case BannerZoneLocation.TOP_SLIDER: {
            return {
                'data-category': Category.BannerTopSlider,
                'data-action': Action.SelectOdd,
            };
        }

        case BannerZoneLocation.RIGHT: {
            return {
                'data-category': Category.BannerRight,
                'data-action': Action.SelectOdd,
            };
        }

        case BannerZoneLocation.RIGHT_SLIDER: {
            return {
                'data-category': Category.BannerRightSlider,
                'data-action': Action.SelectOdd,
            };
        }

        case BannerZoneLocation.RIGHT_TOP_SPORT_EVENTS: {
            return {
                'data-category': Category.TopSportEventsRight,
                'data-action': Action.SelectOdd,
            };
        }

        default: {
            return {
                'data-category': Category.AppCategory,
                'data-action': Action.SelectOdd,
            };
        }
    }
}

export default Banner;
